// Authentication store. Gates the whole app behind a session.
//
// Flow: on boot we probe GET /auth/me. 200 → authenticated; 401 → show the login screen.
// Login posts email + password (+ TOTP); a `mfa_required` response asks for the code.
// Logout clears the session cookie server-side and resets local state.

import * as api from '../api/client';
import { ApiError, clearCsrfToken } from '../api/http';
import type { AuthUser } from '../types/api';
import { Store } from './store';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  mfaRequired: boolean;
  submitting: boolean;
  error: string | null;
  /** Set when a live session expires mid-use, so we can surface a re-auth notice. */
  expired: boolean;
}

export const authStore = new Store<AuthState>({
  status: 'loading',
  user: null,
  mfaRequired: false,
  submitting: false,
  error: null,
  expired: false,
});

/** Probe the session on startup. */
export async function checkSession(): Promise<void> {
  try {
    const user = await api.me();
    authStore.set({ status: 'authenticated', user, error: null, expired: false, mfaRequired: false });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      authStore.set({ status: 'anonymous', user: null });
    } else {
      // Backend not ready yet — treat as anonymous but keep the error for the login screen.
      const message = err instanceof Error ? err.message : 'Unable to reach the server';
      authStore.set({ status: 'anonymous', user: null, error: message });
    }
  }
}

/**
 * Attempt a login. Returns true on full authentication, false when MFA is still required
 * (the caller then reveals the TOTP field).
 */
export async function login(email: string, password: string, totp?: string): Promise<boolean> {
  authStore.set({ submitting: true, error: null });
  try {
    const res = await api.login(email, password, totp);
    if (res.mfa_required && !res.user) {
      authStore.set({ submitting: false, mfaRequired: true, error: null });
      return false;
    }
    if (res.user) {
      authStore.set({
        status: 'authenticated',
        user: res.user,
        submitting: false,
        error: null,
        mfaRequired: false,
        expired: false,
      });
      return true;
    }
    authStore.set({ submitting: false, error: 'Unexpected login response' });
    return false;
  } catch (err) {
    const message = err instanceof ApiError ? err.message : 'Login failed';
    authStore.set({ submitting: false, error: message });
    return false;
  }
}

export async function logout(): Promise<void> {
  try {
    await api.logout();
  } catch {
    /* best-effort: clear locally regardless */
  }
  clearCsrfToken();
  authStore.set({
    status: 'anonymous',
    user: null,
    mfaRequired: false,
    error: null,
    expired: false,
    submitting: false,
  });
}

/** Called by the feed when the server rejects requests with 401 during a live session. */
export function markSessionExpired(): void {
  clearCsrfToken();
  authStore.set({ status: 'anonymous', user: null, expired: true, mfaRequired: false });
}

export function resetAuthError(): void {
  authStore.set({ error: null });
}
